import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  TablePagination,
  Alert,
  Stack
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import moment from 'moment';
import { getTransaction } from '../../lib/connector/paypalQueries';
import { generateAndDownloadReceipt } from './receiptGenerator';

export interface Transaction {
  id: string;
  amount: string;
  status: 'settled' | 'pending' | 'failed' | 'refunded';
  cardType: string;
  cardLast4: string;
  createdAt: string;
  updatedAt: string;
  planId: string;
}

interface BillingHistoryProps {
  transactions: Transaction[];
  userId: string;
}

const BillingHistory: React.FC<BillingHistoryProps> = ({ transactions = [], userId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'settled':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'settled':
        return <CheckCircleIcon fontSize="small" />;
      case 'pending':
        return <ScheduleIcon fontSize="small" />;
      case 'failed':
        return <CancelIcon fontSize="small" />;
      case 'refunded':
        return <WarningIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'settled':
        return <Typography variant="body2">Confirmed</Typography>;
      case 'pending':
        return <Typography variant="body2">Pending</Typography>;
      case 'failed':
        return <Typography variant="body2">Failed</Typography>;
      case 'refunded':
        return <Typography variant="body2">Refunded</Typography>;
      default:
        return null;
    }
  };

  const setStatus = (status: string) => {
    if (status === 'settled') {
      return 'settled'
    } else if (status === 'failed') {
      return 'failed'
    } else if (status === 'authorization_expired') {
      return 'failed'
    } else if (status === 'processor_declined') {
      return 'failed'
    } else if (status === 'gateway_rejected') {
      return 'failed'
    } else if (status === 'voided') {
      return 'failed'
    } else if (status === 'settlement_declined') {
      return 'failed'
    } else {
      return 'pending'
    }
  };

  const getCardIcon = (cardType: string) => {
    // You can replace these with actual card brand icons if needed
    return <CreditCardIcon />;
  };

  const handleDownloadReceipt = async (transactionId: string) => {
    try {
      const response = await getTransaction(userId, transactionId);
      if (!response || !response.body) {
        return;
      }

      const transaction = JSON.parse(response.body);
      await generateAndDownloadReceipt(transaction);
    } catch (error) {
      console.error('Error downloading receipt:', error);
    }
  };

  if (transactions.length === 0) {
    return (
      <Alert severity="info" icon={<ReceiptIcon />}>
        No billing history available. Your transactions will appear here once you make a payment.
      </Alert>
    );
  }

  // Sort transactions by date (newest first)
  const sortedTransactions = [...transactions].sort((a, b) => 
    moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
  );

  const paginatedTransactions = sortedTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Date
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Transaction ID
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Amount
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Payment Method
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Status
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight={600}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.map((transaction) => (
              <TableRow 
                key={transaction.id}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {moment(transaction.createdAt).format('MMM D, YYYY')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {moment(transaction.createdAt).format('h:mm A')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {transaction.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    ${parseFloat(transaction.amount).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                      {getCardIcon(transaction.cardType)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {transaction.cardType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •••• {transaction.cardLast4}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(setStatus(transaction.status))}
                    size="small"
                    color={getStatusColor(setStatus(transaction.status)) as any}
                    icon={getStatusIcon(setStatus(transaction.status))!}
                    sx={{ fontWeight: 600, color: 'background.paper' }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Download Receipt">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadReceipt(transaction.id)}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'primary.light',
                          color: 'primary.main'
                        } 
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {transactions.length > 5 && (
        <TablePagination
          component="div"
          count={transactions.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}
    </Box>
  );
};

export default BillingHistory;